import { useEffect, useState } from "react";
import { X, Sparkle, Trophy, Lightbulb, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../../lib/teleproApi";

/**
 * EndOfDayRecapModal — Vague 11-C.
 *
 * The télépro clicks "🏁 Terminer ma journée" → we call
 * POST /ai/end-of-day → the AI analyses today's calls and returns
 * {day_stats, insights[3], advice, encouragement}.
 *
 * Props :
 *   open, onClose
 */
export default function EndOfDayRecapModal({ open, onClose }) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);

  const trigger = async () => {
    setState({ loading: true, data: null, error: null });
    setEmailed(false);
    try {
      const r = await teleproApi.post("/ai/end-of-day");
      if (r?.data?.insights || r?.data?.day_stats) {
        setState({ loading: false, data: r.data, error: null });
      } else {
        setState({ loading: false, data: null, error: "L'IA n'a pas pu générer le bilan" });
      }
    } catch (e) {
      setState({ loading: false, data: null, error: e.message });
    }
  };

  const sendByEmail = async () => {
    if (emailing) return;
    setEmailing(true);
    try {
      const r = await teleproApi.post("/me/reports/end-of-day");
      const d = r?.data?.delivery || {};
      if (d.sent_to_telepro || d.sent_to_admin) {
        setEmailed(true);
        const bits = [];
        if (d.sent_to_telepro) bits.push("ton email");
        if (d.sent_to_admin) bits.push("l'admin");
        toast.success(`📧 Rapport envoyé sur ${bits.join(" + ")}`);
      } else {
        toast.error("Envoi email impossible pour le moment");
      }
    } catch (e) {
      toast.error("Erreur envoi email : " + (e?.message || "réessaie plus tard"));
    } finally {
      setEmailing(false);
    }
  };

  // Auto-trigger on open
  useEffect(() => {
    if (open && !state.data && !state.loading && !state.error) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const d = state.data;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}/>
      <div
        className="fixed inset-x-3 top-6 bottom-6 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[560px] sm:max-w-[92vw] sm:max-h-[85vh] z-50 bg-card border border-primary/30 rounded-sm shadow-xl overflow-y-auto"
        data-testid="end-of-day-modal"
      >
        <div className="sticky top-0 bg-gradient-to-r from-primary to-secondary text-primary-foreground px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Trophy size={20} weight="fill" className="text-yellow-300 shrink-0"/>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider opacity-80">Bilan de ta journée</div>
              <div className="font-display font-medium text-sm truncate">Ce que tu as fait aujourd'hui</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-primary-foreground/80 hover:text-white p-1"
            data-testid="end-of-day-close"
          >
            <X size={16} weight="bold"/>
          </button>
        </div>

        {state.loading && !d && (
          <div className="p-8 text-center">
            <Sparkle size={32} weight="fill" className="inline text-yellow-500 animate-pulse"/>
            <div className="mt-3 text-sm text-muted-foreground italic">
              L'IA analyse tes appels du jour…
            </div>
          </div>
        )}

        {state.error && (
          <div className="p-6 text-center">
            <div className="text-sm text-red-800 mb-3">❌ {state.error}</div>
            <button
              onClick={trigger}
              className="text-xs font-semibold bg-primary text-primary-foreground rounded-sm px-3 py-1.5"
              data-testid="end-of-day-retry"
            >
              Réessayer
            </button>
          </div>
        )}

        {d && (
          <div className="p-4 space-y-4" data-testid="end-of-day-result">
            {/* Day stats */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {[
                { label: "Appels", val: d.day_stats?.total_calls || 0 },
                { label: "Contacts", val: d.day_stats?.contacts_reached || 0 },
                { label: "Opportunités", val: d.day_stats?.opportunities || 0, accent: true },
                { label: "Ventes", val: d.day_stats?.sales || 0, accent: true },
              ].map((s) => (
                <div key={s.label} className={`text-center rounded-sm px-2 py-2 border ${s.accent ? "bg-primary text-primary-foreground border-primary" : "bg-muted/30 border-border"}`}>
                  <div className={`text-[9px] uppercase tracking-wider font-bold ${s.accent ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{s.label}</div>
                  <div className="font-display text-xl font-medium tabular-nums leading-none mt-0.5">{s.val}</div>
                </div>
              ))}
            </div>

            {/* Insights */}
            {d.insights?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-2 flex items-center gap-1">
                  <Lightbulb size={11} weight="fill" className="text-yellow-500"/> Ce que j'observe
                </div>
                <ul className="space-y-1.5">
                  {d.insights.map((ins, i) => (
                    <li key={i} className="text-sm flex items-start gap-1.5 bg-primary/5 border-l-2 border-primary/40 pl-2 py-1 rounded-sm">
                      <span className="text-primary font-bold shrink-0">•</span>
                      <span className="leading-snug">{ins}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Advice */}
            {d.advice && (
              <div className="bg-secondary/10 border border-secondary/40 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-secondary mb-1 flex items-center gap-1">
                  <ChatCircle size={11} weight="fill"/> Conseil pour demain
                </div>
                <div className="text-sm leading-snug">{d.advice}</div>
              </div>
            )}

            {/* Encouragement */}
            {d.encouragement && (
              <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-sm p-3 text-center">
                <div className="text-sm font-medium italic">"{d.encouragement}"</div>
              </div>
            )}

            <button
              onClick={sendByEmail}
              disabled={emailing || emailed}
              className={`w-full rounded-sm py-2.5 text-sm font-bold border ${emailed ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-primary/40 text-primary hover:bg-primary/5"} ${emailing ? "opacity-60 cursor-wait" : ""}`}
              data-testid="end-of-day-email"
            >
              {emailing ? "Envoi en cours…" : emailed ? "✅ Envoyé (toi + admin)" : "📧 M'envoyer le rapport par email"}
            </button>

            <button
              onClick={() => { toast.success("Bonne soirée ! 🎉"); onClose?.(); }}
              className="w-full bg-primary text-primary-foreground hover:bg-[#0f3d29] rounded-sm py-2.5 text-sm font-bold"
              data-testid="end-of-day-finish"
            >
              🎉 Fermer et profiter de ma soirée
            </button>
          </div>
        )}
      </div>
    </>
  );
}
